import { clsx } from "clsx";
import { cva } from "class-variance-authority";

export const fieldLabelClassname = "tw-text-xs md:tw-text-sm tw-text-black tw-cursor-default";

/** Standard marketing-site focus ring for native inputs and textareas. */
export const fieldFocusClassname = "tw-outline-none focus-visible:tw-outline-focus";

/** Focus ring for composite controls where focus lives on a child element. */
export const fieldGroupFocusClassname = "tw-outline-none has-[:focus-visible]:tw-outline-focus";

export const fieldInputVariants = cva(
  [
    "tw-w-full tw-rounded-xl tw-border tw-border-gray-200 tw-px-4 tw-py-3",
    "tw-text-xs md:tw-text-base tw-text-black placeholder:tw-text-gray-400",
    fieldFocusClassname,
  ],
  {
    variants: {
      type: {
        input: "tw-max-h-11",
        textarea: "tw-min-h-[7.5rem]",
      },
    },
    defaultVariants: {
      type: "input",
    },
  },
);

export const selectTriggerBaseClassname =
  "tw-inline-flex tw-w-full tw-max-h-11 tw-items-center tw-justify-between tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white tw-px-4 tw-py-3 " +
  "tw-text-xs md:tw-text-base tw-text-black tw-outline-none " +
  "disabled:tw-cursor-not-allowed disabled:tw-opacity-50 ";

export const selectTriggerClassname = (isFocusVisible = false) =>
  clsx(selectTriggerBaseClassname, isFocusVisible && "tw-outline-focus");

export const selectSearchFieldWrapperClassname = clsx(
  fieldInputVariants(),
  fieldGroupFocusClassname,
  "tw-flex tw-items-center tw-gap-2 tw-h-9 tw-py-2 tw-px-3 tw-text-sm",
);

export const selectContentClassname =
  "tw-relative tw-z-50 tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white tw-p-1 tw-shadow-lg";

export const selectItemClassname =
  "tw-relative tw-flex tw-min-h-9 tw-w-full tw-cursor-pointer tw-items-center tw-px-3 tw-py-2 tw-outline-none " +
  "tw-text-xs md:tw-text-base tw-text-black";

export const selectItemStateClassname = ({
  isSelected,
  isHovered,
  isFocusVisible,
}: {
  isSelected?: boolean;
  isHovered?: boolean;
  isFocusVisible?: boolean;
}) =>
  clsx(
    selectItemClassname,
    "tw-rounded-lg",
    isSelected && "tw-font-semibold",
    (isHovered || isFocusVisible) && "tw-bg-gray-100",
    isFocusVisible && "tw-outline-focus",
  );

export const phoneInputContainerClassname = clsx(
  "tw-flex tw-items-center tw-gap-0 tw-w-full",
  "tw-h-11 tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white",
  fieldGroupFocusClassname,
);

export const phoneInputCountryButtonBaseClassname =
  "tw-border-0 tw-shrink-0 tw-bg-transparent tw-rounded-l-xl tw-items-center tw-justify-between tw-flex tw-overflow-hidden tw-h-full tw-self-stretch tw-relative tw-z-10 tw-outline-none";

const _phoneInputCountryButtonCompact =
  phoneInputCountryButtonBaseClassname + " tw-px-3 tw-min-w-14 sm:tw-px-4 sm:tw-min-w-32 sm:tw-max-w-40";
const _phoneInputCountryButtonDefault = phoneInputCountryButtonBaseClassname + " tw-px-4 tw-min-w-32 tw-max-w-40";

export const phoneInputCountryButtonClassname = (compactOnMobile?: boolean) =>
  compactOnMobile ? _phoneInputCountryButtonCompact : _phoneInputCountryButtonDefault;

export const phoneInputFieldClassname = clsx(
  "tw-border-0 tw-bg-transparent tw-rounded-r-xl tw-flex-1 tw-min-w-0 tw-h-full",
  "tw-px-4 tw-text-sm tw-relative tw-z-10 placeholder:tw-text-gray-400 tw-outline-none",
);

export const fieldErrorClassname = "tw-text-red-800 tw-text-sm tw-mt-0.5 tw-cursor-default";

export const testVariantPrimaryButtonClassname = clsx(
  "tw-h-11 tw-rounded-[34px] tw-bg-black tw-px-5 tw-type-base tw-text-[#fafafa]",
  "enabled:hover:tw-bg-black disabled:tw-opacity-40",
);

export const testVariantBackButtonClassname = clsx(
  "tw-h-11 tw-shrink-0 tw-rounded-[34px] tw-bg-cream tw-px-6",
  "enabled:hover:tw-bg-cream",
);

export const testVariantFooterActionRowClassname = "tw-flex tw-h-11 tw-w-full tw-shrink-0 tw-items-center";

export const testVariantFooterLinkClassname = clsx(
  "tw-underline tw-decoration-solid tw-rounded-sm",
  fieldFocusClassname,
);
